/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { fetchPortfolioSnapshot, fetchAccounts } from '../api/api';
import API_BASE_URL from '../api/config';

// usePortfolioSocket
// Contract
// inputs: accountIds?: string[] | undefined
// outputs: {
//   ready: boolean,
//   accountsById: Record<string, { balance?: number, equity?: number, margin?: number, currency?: string }>,
//   positionsByAccount: Record<string, any[]>,
//   pendingByAccount: Record<string, any[]>,
//   lastEventAt?: number,
//   reconnect: () => void,
//   error?: Error | null,
// }
export default function usePortfolioSocket({ accountIds, enabled = true } = {}) {
  const [accountsById, setAccountsById] = useState({});
  const [positionsByAccount, setPositionsByAccount] = useState({});
  const [pendingByAccount, setPendingByAccount] = useState({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [lastEventAt, setLastEventAt] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const connectSeq = useRef(0);

  const accountsParam = useMemo(() => {
    if (!Array.isArray(accountIds) || !accountIds.length) return '';
    return accountIds.join(',');
  }, [accountIds]);

  const cleanupSocket = useCallback(() => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
      wsRef.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (!enabled) {
      // Ensure any existing socket is closed when disabled
      cleanupSocket();
      setReady(false);
      return;
    }
    cleanupSocket();
    setError(null);
    setReady(false);

    const seq = ++connectSeq.current;

    // 1) Seed from snapshot + enrich with account names
  try {
      console.log('[PortfolioSocket] fetching snapshot', { accountsParam, accountIds });
      const [snapRes, accRes] = await Promise.allSettled([
        fetchPortfolioSnapshot(accountIds),
        fetchAccounts(),
      ]);
      const snapshot = snapRes.status === 'fulfilled' ? snapRes.value : null;
      const accountsListRaw = accRes.status === 'fulfilled' ? accRes.value : [];
      const accountsList = Array.isArray(accountsListRaw)
        ? accountsListRaw
        : (Array.isArray(accountsListRaw?.results) ? accountsListRaw.results : (Array.isArray(accountsListRaw?.data) ? accountsListRaw.data : []));
      if (connectSeq.current !== seq) return; // stale

      const accountsMap = {};
      const posMap = {};
      const pendMap = {};

      const nameById = {};
      for (const a of accountsList) {
        if (!a) continue;
        const id = a.id || a.accountId || a.uuid;
        if (id) nameById[id] = a.name || a.label || a.display_name || id;
      }

    (snapshot?.accounts || []).forEach((acc) => {
        accountsMap[acc.accountId] = {
          balance: acc.balance,
          equity: acc.equity,
          margin: acc.margin,
          currency: acc.currency,
          platform: acc.platform,
      name: nameById[acc.accountId] || acc.name || acc.account_name || acc.label || acc.accountId,
        };
      });
      (snapshot?.positions || []).forEach((p) => {
        const id = p.accountId;
        if (!posMap[id]) posMap[id] = [];
        posMap[id].push(p);
      });
      (snapshot?.pending_orders || []).forEach((o) => {
        const id = o.accountId;
        if (!pendMap[id]) pendMap[id] = [];
        pendMap[id].push(o);
      });

      setAccountsById(accountsMap);
      setPositionsByAccount(posMap);
      setPendingByAccount(pendMap);
      console.log('[PortfolioSocket] snapshot applied', {
        accounts: Object.keys(accountsMap).length,
        positions: Object.values(posMap).reduce((n, arr) => n + (arr?.length || 0), 0),
        pending: Object.values(pendMap).reduce((n, arr) => n + (arr?.length || 0), 0),
      });
    } catch (e) {
      // Proceed; WS may still populate data, but surface error
      console.warn('[PortfolioSocket] snapshot failed', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch snapshot'));
    }

    // 2) Open WebSocket
  const token = localStorage.getItem('token');
    if (!token) {
      setError(new Error('Missing auth token'));
      return;
    }

    // Derive WS base:
    // Priority 1: VITE_WS_BASE_URL (full origin like ws(s)://host:port)
    // Priority 2: API_BASE_URL (http(s)://host:port) -> convert to ws(s)
    // Fallback: window.location
    const envWsBase = import.meta.env.VITE_WS_BASE_URL;
    let wsOrigin;
    if (envWsBase) {
      wsOrigin = envWsBase.replace(/\/$/, '');
    } else if (API_BASE_URL) {
      try {
        const httpUrl = new URL(API_BASE_URL);
        const wsProto = httpUrl.protocol === 'https:' ? 'wss:' : 'ws:';
        wsOrigin = `${wsProto}//${httpUrl.host}`;
      } catch (e) {
        console.warn('[PortfolioSocket] Invalid API_BASE_URL, falling back to window.location', API_BASE_URL, e);
      }
    }
    if (!wsOrigin) {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsOrigin = `${proto}//${window.location.host}`;
    }

    const qs = new URLSearchParams({ token });
    if (accountsParam) qs.set('accounts', accountsParam);
    const url = `${wsOrigin}/ws/portfolio?${qs.toString()}`;

    console.log('[PortfolioSocket] opening ws', { url, tokenLen: token?.length, accountsParam, wsOrigin, API_BASE_URL });

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (connectSeq.current !== seq) return;
      console.log('[PortfolioSocket] ws open');
      // will mark ready after server confirms
    };

    ws.onmessage = (ev) => {
      if (connectSeq.current !== seq) return; // stale connection
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        console.warn('[PortfolioSocket] non-JSON frame', ev.data);
        return;
      }
      if (!msg || typeof msg !== 'object') return;
      const { type } = msg;
      console.log('[PortfolioSocket] message', type, msg);
      setLastEventAt(Date.now());

      if (type === 'portfolio_ready') {
        setReady(true);
        return;
      }
      if (type === 'balance_update') {
        const { accountId, data } = msg;
        if (!accountId || !data) return;
        setAccountsById((prev) => ({
          ...prev,
          [accountId]: { ...(prev[accountId] || {}), ...data },
        }));
        if (!ready) setReady(true);
        return;
      }
      if (type === 'position_update') {
        const { accountId, data } = msg;
        const items = data?.open_positions || [];
        if (!accountId) return;
        setPositionsByAccount((prev) => ({ ...prev, [accountId]: items }));
        if (!ready) setReady(true);
        return;
      }
      if (type === 'pending_orders_update') {
        const { accountId, data } = msg;
        const items = data?.pending_orders || [];
        if (!accountId) return;
        setPendingByAccount((prev) => ({ ...prev, [accountId]: items }));
        if (!ready) setReady(true);
        return;
      }
    };

    ws.onclose = (ev) => {
      if (connectSeq.current !== seq) return;
      console.warn('[PortfolioSocket] ws close', { code: ev.code, reason: ev.reason, wasClean: ev.wasClean });
      setReady(false);
      // Server redeploying — reconnect immediately
      if (ev.code === 4000) {
        console.log('[PortfolioSocket] Server redeploying, reconnecting immediately...');
        reconnectTimer.current = setTimeout(() => {
          connect();
        }, 100);
        return;
      }
      // backoff reconnect
      reconnectTimer.current = setTimeout(() => {
        connect();
      }, 1500);
    };

    ws.onerror = (err) => {
      console.error('[PortfolioSocket] ws error', err);
      // errors are handled via close/reconnect
    };
  }, [accountsParam, cleanupSocket, enabled]);

  useEffect(() => {
    connect();
    // No cleanup on unmount — connection persists across page navigation.
    // Cleanup happens on beforeunload (see below).
  }, [connect, enabled]);

  // Close WebSocket only when user leaves the website
  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanupSocket();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Full component tree teardown (app unmount) — also clean up
      cleanupSocket();
      connectSeq.current += 1;
    };
  }, [cleanupSocket]);

  const reconnect = useCallback(() => {
    connect();
  }, [connect]);

  return {
    ready,
    accountsById,
    positionsByAccount,
    pendingByAccount,
    lastEventAt,
    reconnect,
    error,
  };
}
